import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, Input, OnDestroy, QueryList, ViewChild, ViewChildren } from '@angular/core';

import { BehaviorSubject, filter, Subscription, take } from 'rxjs';
import { MarkerAreaEvent, ResizeGrip } from 'markerjs2';

import { AbstractDialogComponent } from '../../../../Core/Dialogs/components/AbstractDialog/abstract-dialog.component';
import { CoreDialogService } from '../../../../Core/Dialogs/services/dialog.service';
import { LocalizationService } from '../../../../Core/Localization/services/localization.service';

import { AdvancedColorPickerPopoverComponent } from '../../../../Common/Forms/components/AdvancedColorPickerPopover/advanced-color-picker-popover.component';
import { Am5Chart } from '../../../../Common/Charts/models/am5-chart.model';
import { ChartAnnotationMarkerStyleTypesEnum, ChartAnnotationMarkerTypesEnum } from './chart-annotation-marker-types.enum';
import { ChartAnnotationOpacityPanel } from './ToolboxPanels/chart-annotation-opacity-panel.model';
import { ChartAnnotations } from '../../../../Common/Reports/models/chart-annotations.model';
import { ChartDisplayElementModel } from '../../../../Common/Reports/models/chart-display-element.model';
import { ChartSeries } from '../../../../Common/Charts/models/chart-series.model';
import { DropdownComponent } from '../../../../Common/Forms/components/Dropdown/dropdown.component';
import { DropdownOptionModel, MultiSelectDropdownOptionGroup } from '../../../../Common/Forms/models/dropdown-option.model';
import { RadioComponent } from '../../../../Common/Forms/components/Radio/radio.component';
import { RadioTypesEnum } from '../../../../Common/Forms/enums/radio-types.enum';
import { ReportSeriesModel } from '../../../../Common/Reports/models/report-series.model';
import { SliderInputComponent } from '../../../../Common/Forms/components/SliderInput/slider-input.component';
import { textStyleFontDropdownOptions } from '../../../../Common/PageBuilder/models/text-style-font-dropdown-options.model';

import { ChartAnnotationArrowTypePanel } from './ToolboxPanels/chart-annotation-arrow-type-panel.model';
import { ChartAnnotationColorPickerPanel } from './ToolboxPanels/chart-annotation-color-picker-panel';
import { ChartAnnotationFontFamilyPanel } from './ToolboxPanels/chart-annotation-font-family-panel.model';
import { ChartAnnotationLineStylePanel } from './ToolboxPanels/chart-annotation-line-style-panel.model';
import { ChartAnnotationLineWidthPanel } from './ToolboxPanels/chart-annotation-line-width-panel.model';
import { ChartAnnotationIndicatorsService } from './chart-annotation-indicators.service';
import { ConfirmDialogComponent } from '../ConfirmDialog/confirm-dialog.component';


@Component({
    selector: 'agpr-chart-annotation-dialog',
    templateUrl: './chart-annotation-dialog.component.html',
    styleUrls: ['./chart-annotation-dialog.component.scss'],
    standalone: false
})
export class ChartAnnotationDialogComponent extends AbstractDialogComponent implements AfterViewInit, OnDestroy {

    @Input() chart: Am5Chart;
    @Input() reportSeries: ReportSeriesModel[];
    @Input() displayElement: ChartDisplayElementModel;
    @Input() containerWidth: number;
    @Input() chartContainerWidth: number;
    @Input() chartBlockPadding: number = 4;
    @Input() plotTop: number;
    @Input() plotBottom: number;

    actionCollectionOverflow: boolean = false;
    actionCollectionExpanded: boolean = false;
    markerCollectionOverflow: boolean = false;
    markerCollectionExpanded: boolean = false;
    lineCollectionExpanded: boolean = false;
    shapeCollectionExpanded: boolean = false;
    textCollectionExpanded: boolean = false;
    indicatorGroups: MultiSelectDropdownOptionGroup[] = [];
    width: number;
    markerArea: any;
    selectedIndicators: any[];
    originalChartSeries: ChartSeries[];
    originalChartAnnotations: ChartAnnotations;
    originalIndicators: any[];
    isRender: boolean = false;
    currentMarkerIconClass: string = '';

    selectedFreehandLineWidth: number;
    selectedLineStrokeWidth: number;
    selectedLineLineStyle: string;
    selectedArrowStrokeWidth: number;
    selectedArrowLineStyle: string;
    selectedArrowArrowType: string;
    selectedCurveStrokeWidth: number;
    selectedCurveLineStyle: string;
    selectedFrameStrokeWidth: number;
    selectedFrameLineStyle: string;
    selectedEllipseStrokeWidth: number;
    selectedEllipseLineStyle: string;

    fontFamilyDropdownOptions: DropdownOptionModel[];
    subscriptions: Subscription[] = [];

    readonly radioTypes = RadioTypesEnum;
    readonly chartAnnotationMarkerStyleTypes = ChartAnnotationMarkerStyleTypesEnum;
    readonly chartAnnotationMarkerTypes = ChartAnnotationMarkerTypesEnum;
    readonly availableMarkerTypes = Object.values(ChartAnnotationMarkerTypesEnum);
    readonly lineCollectionMarkerTypes = [
        ChartAnnotationMarkerTypesEnum.LINE_MARKER,
        ChartAnnotationMarkerTypesEnum.ARROW_MARKER,
        ChartAnnotationMarkerTypesEnum.CURVE_MARKER
    ];
    readonly shapeCollectionMarkerTypes = [
        ChartAnnotationMarkerTypesEnum.FRAME_MARKER,
        ChartAnnotationMarkerTypesEnum.ELLIPSE_MARKER,
        ChartAnnotationMarkerTypesEnum.HIGHLIGHT_MARKER
    ];
    readonly textCollectionMarkerTypes = [
        ChartAnnotationMarkerTypesEnum.TEXT_MARKER,
        ChartAnnotationMarkerTypesEnum.CALLOUT_MARKER
    ];
    readonly lineWidthOptions: { value: number }[] = Array.from({ length: 10 }, (_, i) => i + 1).map(num => {
        return {
            value: num,
            icon: `fa-kit fa-agpr-line-weight-${num}px`
        };
    });
    readonly lineStyleOptions: { value: string; icon: string }[] = [
        {
            value: 'none',
            icon: 'fa-kit fa-agpr-line-style-solid'
        },
        {
            value: '3 2',
            icon: 'fa-kit fa-agpr-line-style-dashed-1'
        },
        {
            value: '6 3',
            icon: 'fa-kit fa-agpr-line-style-dashed-2'
        },
        {
            value: '9 4',
            icon: 'fa-kit fa-agpr-line-style-dashed-3'
        }
    ];
    readonly arrowTypeOptions: { value: string; icon: string }[] = [
        {
            value: 'start',
            icon: 'fa-solid fa-arrow-left'
        },
        {
            value: 'both',
            icon: 'fa-solid fa-arrows-left-right'
        },
        {
            value: 'end',
            icon: 'fa-solid fa-arrow-right'
        }
    ];
    readonly markerTypesToMarkerIconClassesMap = new Map<ChartAnnotationMarkerTypesEnum, string>([
        [ChartAnnotationMarkerTypesEnum.FREEHAND_MARKER, 'fa-duotone fa-regular fa-marker'],
        [ChartAnnotationMarkerTypesEnum.LINE_MARKER, 'fa-solid fa-dash'],
        [ChartAnnotationMarkerTypesEnum.ARROW_MARKER, 'fa-solid fa-arrows-left-right'],
        [ChartAnnotationMarkerTypesEnum.CURVE_MARKER, 'fa-duotone fa-regular fa-bezier-curve'],
        [ChartAnnotationMarkerTypesEnum.FRAME_MARKER, 'fa-duotone fa-regular fa-square'],
        [ChartAnnotationMarkerTypesEnum.ELLIPSE_MARKER, 'fa-duotone fa-regular fa-circle'],
        [ChartAnnotationMarkerTypesEnum.HIGHLIGHT_MARKER, 'fa-duotone fa-regular fa-highlighter'],
        [ChartAnnotationMarkerTypesEnum.TEXT_MARKER, 'fa-solid fa-t'],
        [ChartAnnotationMarkerTypesEnum.CALLOUT_MARKER, 'fa-duotone fa-regular fa-comment-text']
    ]);
    readonly chartBlockSpace = 4;

    @ViewChild('container') container: ElementRef;
    @ViewChild('chartContainer') chartContainer: ElementRef;
    // todo: a11y
    // @ViewChild('selectButton', { read: ElementRef }) selectButton: ElementRef;
    @ViewChild('actionCollectionButton', { read: ElementRef }) actionCollectionButton: ElementRef;
    @ViewChild('actionCollectionRibbon') actionCollectionRibbon: ElementRef;
    @ViewChild('currentMarkerIcon') currentMarkerIcon: ElementRef;
    @ViewChild('markerCollectionButton', { read: ElementRef }) markerCollectionButton: ElementRef;
    @ViewChild('markerCollectionRibbon') markerCollectionRibbon: ElementRef;
    @ViewChild('lineCollectionButton', { read: ElementRef }) lineCollectionButton: ElementRef;
    @ViewChild('lineCollectionRibbon') lineCollectionRibbon: ElementRef;
    @ViewChild('shapeCollectionButton', { read: ElementRef }) shapeCollectionButton: ElementRef;
    @ViewChild('shapeCollectionRibbon') shapeCollectionRibbon: ElementRef;
    @ViewChild('textCollectionButton', { read: ElementRef }) textCollectionButton: ElementRef;
    @ViewChild('textCollectionRibbon') textCollectionRibbon: ElementRef;

    @ViewChild('freehandColorPicker') freehandColorPicker: AdvancedColorPickerPopoverComponent;
    @ViewChild('freehandLineWidthRadioGroup') freehandLineWidthRadioGroup: ElementRef;

    @ViewChild('lineStrokeColorPicker') lineStrokeColorPicker: AdvancedColorPickerPopoverComponent;
    @ViewChild('lineStrokeWidthRadioGroup') lineStrokeWidthRadioGroup: ElementRef;
    @ViewChild('lineLineStyleRadioGroup') lineLineStyleRadioGroup: ElementRef;
    @ViewChild('arrowStrokeColorPicker') arrowStrokeColorPicker: AdvancedColorPickerPopoverComponent;
    @ViewChild('arrowStrokeWidthRadioGroup') arrowStrokeWidthRadioGroup: ElementRef;
    @ViewChild('arrowLineStyleRadioGroup') arrowLineStyleRadioGroup: ElementRef;
    @ViewChild('arrowArrowTypeRadioGroup') arrowArrowTypeRadioGroup: ElementRef;
    @ViewChild('curveStrokeColorPicker') curveStrokeColorPicker: AdvancedColorPickerPopoverComponent;
    @ViewChild('curveStrokeWidthRadioGroup') curveStrokeWidthRadioGroup: ElementRef;
    @ViewChild('curveLineStyleRadioGroup') curveLineStyleRadioGroup: ElementRef;

    @ViewChild('frameStrokeColorPicker') frameStrokeColorPicker: AdvancedColorPickerPopoverComponent;
    @ViewChild('frameStrokeWidthRadioGroup') frameStrokeWidthRadioGroup: ElementRef;
    @ViewChild('frameLineStyleRadioGroup') frameLineStyleRadioGroup: ElementRef;
    @ViewChild('ellipseStrokeColorPicker') ellipseStrokeColorPicker: AdvancedColorPickerPopoverComponent;
    @ViewChild('ellipseFillColorPicker') ellipseFillColorPicker: AdvancedColorPickerPopoverComponent;
    @ViewChild('ellipseStrokeWidthRadioGroup') ellipseStrokeWidthRadioGroup: ElementRef;
    @ViewChild('ellipseLineStyleRadioGroup') ellipseLineStyleRadioGroup: ElementRef;
    @ViewChild('ellipseOpacitySliderInput') ellipseOpacitySliderInput: SliderInputComponent;
    @ViewChild('highlightFillColorPicker') highlightFillColorPicker: AdvancedColorPickerPopoverComponent;
    @ViewChild('highlightOpacitySliderInput') highlightOpacitySliderInput: SliderInputComponent;

    @ViewChild('textColorPicker') textColorPicker: AdvancedColorPickerPopoverComponent;
    @ViewChild('textFontFamilyDropdown') textFontFamilyDropdown: DropdownComponent;
    @ViewChild('calloutColorPicker') calloutColorPicker: AdvancedColorPickerPopoverComponent;
    @ViewChild('calloutBgColorPicker') calloutBgColorPicker: AdvancedColorPickerPopoverComponent;
    @ViewChild('calloutFontFamilyDropdown') calloutFontFamilyDropdown: DropdownComponent;

    @ViewChildren(RadioComponent) radioComponents: QueryList<RadioComponent>;

    @ViewChild('indicatorCollectionDropdown', { read: ElementRef }) indicatorCollectionDropdown: ElementRef;

    constructor(
        public coreDialogService: CoreDialogService,
        public localizationService: LocalizationService,
        public chartAnnotationIndicatorsService: ChartAnnotationIndicatorsService,
        public cd: ChangeDetectorRef
    ) {
        super(coreDialogService, 'ChartAnnotation');
    }

    ngAfterViewInit(): void {
        this.originalChartSeries = this.chart.series.slice();
        this.originalIndicators = JSON.parse(JSON.stringify(this.displayElement.chartAnnotations?.indicators || []));
        this.chart.config.useTooltip = false;
        this.setIndicatorGroups();
        const width = Math.max((this.chartContainerWidth || this.chart.settings.width) - this.chartBlockPadding, 255);
        this.width = width;
        const height = this.plotBottom - this.plotTop;
        this.container.nativeElement.style.width = width + 'px';
        this.container.nativeElement.style.height = height + 30 + 100 + 'px';
        if (this.chartContainerWidth) {
            this.chartContainer.nativeElement.style.width = this.chartContainerWidth - this.chartBlockPadding + 'px';
        } else {
            this.chartContainer.nativeElement.style.width = width + 'px';
        }
        this.chart.annotatorConfigured$.pipe(
            filter(x => !!x),
            take(1)
        ).subscribe(x => {
            this.chart.annotator.getMarkerArea().then(area => {
                area.setupResizeObserver = function() {};
                area.availableMarkerTypes = this.availableMarkerTypes;
                area.addEventListener('show', (a) => {
                    this.markerArea = a.markerArea;
                    const coverDiv = a.markerArea.coverDiv;
                    coverDiv.style.height = height + 'px';
                    const toolbarDiv = a.markerArea.toolbar.uiContainer;
                    toolbarDiv.style.position = 'absolute';
                    toolbarDiv.style.height = '50px';
                    toolbarDiv.style.overflow = 'visible';
                    toolbarDiv.style.zIndex = '2';
                    toolbarDiv.style.boxShadow = 'none';
                    toolbarDiv.style.borderBottom = '1px solid #ced4da';
                    toolbarDiv.style.top = '0';
                    toolbarDiv.style.left = '0';
                    toolbarDiv.style.borderRadius = '12px 12px 0 0';
                    a.markerArea.toolbar.overflowButton.remove();
                    const toolboxDiv = a.markerArea.toolbox.uiContainer;
                    toolboxDiv.style.zIndex = '3';
                    toolboxDiv.style.height = '50px';
                    toolboxDiv.style.marginTop = '14px';
                    toolboxDiv.style.position = 'absolute';
                    toolboxDiv.style.left = '0';
                    toolboxDiv.style.bottom = '0';
                    toolboxDiv.style.borderRadius = '0 0 12px 12px';
                    toolboxDiv.style.borderTop = '1px solid #ced4da';
                    a.markerArea.uiDiv.style.gap = '15px';

                    this.insertLineCollectionButton(a.markerArea.toolbar);
                    this.insertShapeCollectionButton(a.markerArea.toolbar);
                    this.insertTextCollectionButton(a.markerArea.toolbar);
                    this.insertIndicatorCollectionButton(a.markerArea.toolbar);
                    this.styleToolbar(a.markerArea.toolbar);
                    this.handleToolbarResponsiveness(a.markerArea, width);
                    this.handleToolboxIconClassReset(a.markerArea);
                });
                area.addEventListener('markercreating', (event) => {
                    this.markerSelected(event);
                    this.styleActionButtons();
                    this.styleToolbox(area);
                });
                area.addEventListener('markerselect', (event) => {
                    this.markerSelected(event);
                    this.styleActionButtons();
                    this.styleToolbox(area);
                    this.styleControlRect(event.marker);
                    this.styleGrip(event.marker);
                });
                area.addEventListener('markerdeselect', (event) => {
                    this.markerDeselected(event);
                    this.styleActionButtons();
                    this.styleToolbox(area);
                    this.updateCurrentMarkerIconClass();
                    const markerArea = event.markerArea;
                    markerArea.currentMarker.toolboxPanels.forEach(panel => {
                        if (panel.subscription) {
                            panel.subscription.unsubscribe();
                        }
                    });
                });
                area.addEventListener('render', (event) => this.isRender = true);
                area.addEventListener('beforeclose', (event) => {
                    const indicators = this.selectedIndicators;
                    const markerState = area.getState();
                    const chartAnnotations = new ChartAnnotations(indicators, markerState);
                    if (!chartAnnotations.isEqual(this.originalChartAnnotations)) {
                        (event as MarkerAreaEvent).preventDefault();
                        const ioModel = {
                            inputs: {
                                id: 'unsavedChartAnnotations',
                                heading: 'DeactivateGuard.heading',
                                messageHtml: 'DeactivateGuard.message',
                                okLabel: 'continue'
                            },
                            outputs: {
                                dialogClose: (confirmed: boolean) => {
                                    if (confirmed) {
                                        area.close(true);
                                    }
                                }
                            }
                        };
                        this.coreDialogService.open(ConfirmDialogComponent, ioModel);
                    }
                });
                area.addEventListener('close', (event) => {
                    const indicators = this.isRender ? this.selectedIndicators : this.originalIndicators;
                    const markerState = this.isRender ? area.getState() : this.chart.annotator.get('markerState');
                    const chartAnnotations = new ChartAnnotations(indicators, markerState);
                    this.chart.config.useTooltip = true;
                    this.closeDialog(chartAnnotations);
                });
                area.show();
                if (this.chart.settings.chartAnnotations.markerState) {
                    area.restoreState(this.chart.settings.chartAnnotations.markerState);
                }
                this.originalChartAnnotations = new ChartAnnotations(this.originalIndicators, area.getState());
                this.markerArea = area;
                this.chartContainer.nativeElement.appendChild(this.markerArea.toolbar.uiContainer);
                this.chartContainer.nativeElement.appendChild(this.markerArea.toolbox.uiContainer);
            });
        });
        this.chart.dataUpdated$.pipe(
            filter(x => !!x),
            take(1)
        ).subscribe(x => this.setSelectedIndicators());
    }

    ngOnDestroy() {
        this.subscriptions.forEach(x => x.unsubscribe());
    }

    setIndicatorGroups(): void {
        this.indicatorGroups = this.chartAnnotationIndicatorsService.getIndicatorGroups(this.chart);
    }

    setSelectedIndicators(): void {
        this.selectedIndicators = this.chart.settings.chartAnnotations?.indicators?.filter(x => {
            const options = this.indicatorGroups.flatMap(g => g.options);
            const selectedIndicator = options.find(o => o.value.indicatorType === x.value.indicatorType && o.value.metric === x.value.metric);
            if (selectedIndicator) {
                selectedIndicator.selected = true;
            }
            return !!selectedIndicator;
        }) || [];
        this.updateIndicators(this.selectedIndicators);
    }

    styleToolbar(markerArea: any): void {
        const toolbar = markerArea.uiContainer;

        const buttonBlocks = Array.from(toolbar.querySelectorAll('[class$="_toolbar-block"]'));

        const actionButtonBlock = buttonBlocks[0] as HTMLDivElement;
        actionButtonBlock.classList.add('agpr-chartAnnotationDialog-toolbarBlock');

        const selectButton = actionButtonBlock.children[0];
        selectButton.innerHTML = '';
        selectButton.classList.add(
            'agpr-chartAnnotationDialog-toolbarButton',
            'agpr-chartAnnotationDialog-toolbarButton-priorityWeightClass',
            'fa-duotone',
            'fa-regular',
            'fa-arrow-pointer'
        );
        // todo: a11y
        // selectButton.replaceWith(this.selectButton.nativeElement);
        const deleteButton = actionButtonBlock.children[1];
        deleteButton.innerHTML = '';
        deleteButton.classList.add(
            'agpr-chartAnnotationDialog-toolbarButton',
            'agpr-chartAnnotationDialog-toolbarButton-priorityWeightClass',
            'agpr-chartAnnotationDialog-toolbarButton--disabled',
            'fa-duotone',
            'fa-regular',
            'fa-trash-can'
        );
        const undoButton = actionButtonBlock.children[2];
        undoButton.innerHTML = '';
        undoButton.classList.add(
            'agpr-chartAnnotationDialog-toolbarButton',
            'agpr-chartAnnotationDialog-toolbarButton-priorityWeightClass',
            'agpr-chartAnnotationDialog-toolbarButton--disabled',
            'fa-solid',
            'fa-arrow-rotate-left'
        );
        const redoButton = actionButtonBlock.children[3];
        redoButton.innerHTML = '';
        redoButton.classList.add(
            'agpr-chartAnnotationDialog-toolbarButton',
            'agpr-chartAnnotationDialog-toolbarButton-priorityWeightClass',
            'agpr-chartAnnotationDialog-toolbarButton--disabled',
            'fa-solid',
            'fa-arrow-rotate-right'
        );

        const markerButtonBlock = buttonBlocks[1] as HTMLDivElement;
        markerButtonBlock.classList.add('agpr-chartAnnotationDialog-toolbarBlock');
        if (this.width >= 430) {
            const freehandMarkerButton = markerButtonBlock.children[0];
            freehandMarkerButton.innerHTML = '';
            freehandMarkerButton.classList.add(
                'agpr-chartAnnotationDialog-toolbarButton',
                'agpr-chartAnnotationDialog-toolbarButton-priorityWeightClass',
                'fa-duotone',
                'fa-regular',
                'fa-marker'
            );
        }

        const resultButtonBlock = buttonBlocks[2] as HTMLDivElement;
        resultButtonBlock.classList.add('agpr-chartAnnotationDialog-toolbarBlock');
        const saveButton = resultButtonBlock.children[0];
        saveButton.innerHTML = '';
        saveButton.classList.add(
            'agpr-chartAnnotationDialog-toolbarButton',
            'agpr-chartAnnotationDialog-toolbarButton-priorityWeightClass',
            'fa-duotone',
            'fa-regular',
            'fa-floppy-disk'
        );
        const cancelButton = resultButtonBlock.children[1];
        cancelButton.innerHTML = '';
        cancelButton.classList.add(
            'agpr-chartAnnotationDialog-toolbarButton',
            'agpr-chartAnnotationDialog-toolbarButton-priorityWeightClass',
            'fa-solid',
            'fa-xmark'
        );
    }

    insertLineCollectionButton(toolbar: any): void {
        const typesToIconClassesMap: Map<ChartAnnotationMarkerTypesEnum, string[]> = new Map([
            [ChartAnnotationMarkerTypesEnum.LINE_MARKER, ['fa-solid', 'fa-dash']],
            [ChartAnnotationMarkerTypesEnum.ARROW_MARKER, ['fa-solid', 'fa-arrows-left-right']],
            [ChartAnnotationMarkerTypesEnum.CURVE_MARKER, ['fa-duotone', 'fa-regular', 'fa-bezier-curve']]
        ]);
        const lineCollectionButton = this.lineCollectionButton.nativeElement;
        const lineCollectionRibbon = this.lineCollectionRibbon.nativeElement;
        this.lineCollectionMarkerTypes.forEach((type) => {
            const button = toolbar.markerButtons.find(x => x.dataset.typeName === type);
            if (button) {
                button.innerHTML = '';
                button.classList.add(
                    'agpr-chartAnnotationDialog-toolbarButton',
                    'agpr-chartAnnotationDialog-toolbarButton-priorityWeightClass',
                    'agpr-chartAnnotationDialog-toolbarButton--rotated',
                );
                typesToIconClassesMap.get(type).forEach(x => button.classList.add(x));
                lineCollectionRibbon.appendChild(button);
            }
        });
        toolbar.markerButtonBlock.appendChild(lineCollectionButton);
        toolbar.markerButtonBlock.appendChild(lineCollectionRibbon);
    }

    toggleLineCollection(): void {
        this.lineCollectionExpanded = !this.lineCollectionExpanded;
        this.actionCollectionExpanded = false;
        this.shapeCollectionExpanded = false;
        this.textCollectionExpanded = false;
    }

    insertShapeCollectionButton(toolbar: any): void {
        const typesToIconClassesMap: Map<ChartAnnotationMarkerTypesEnum, string[]> = new Map([
            [ChartAnnotationMarkerTypesEnum.FRAME_MARKER, ['fa-duotone', 'fa-regular', 'fa-square']],
            [ChartAnnotationMarkerTypesEnum.ELLIPSE_MARKER, ['fa-duotone', 'fa-regular', 'fa-circle']],
            [ChartAnnotationMarkerTypesEnum.HIGHLIGHT_MARKER, ['fa-duotone', 'fa-regular', 'fa-highlighter']]
        ]);
        const shapeCollectionButton = this.shapeCollectionButton.nativeElement;
        const shapeCollectionRibbon = this.shapeCollectionRibbon.nativeElement;
        this.shapeCollectionMarkerTypes.forEach((type) => {
            const button = toolbar.markerButtons.find(x => x.dataset.typeName === type);
            if (button) {
                button.innerHTML = '';
                button.classList.add(
                    'agpr-chartAnnotationDialog-toolbarButton',
                    'agpr-chartAnnotationDialog-toolbarButton-priorityWeightClass'
                );
                typesToIconClassesMap.get(type).forEach(x => button.classList.add(x));
                shapeCollectionRibbon.appendChild(button);
            }
        });
        toolbar.markerButtonBlock.appendChild(shapeCollectionButton);
        toolbar.markerButtonBlock.appendChild(shapeCollectionRibbon);
    }

    toggleShapeCollection(): void {
        this.shapeCollectionExpanded = !this.shapeCollectionExpanded;
        this.actionCollectionExpanded = false;
        this.lineCollectionExpanded = false;
        this.textCollectionExpanded = false;
    }

    insertTextCollectionButton(toolbar: any): void {
        const typesToIconClassesMap: Map<ChartAnnotationMarkerTypesEnum, string[]> = new Map([
            [ChartAnnotationMarkerTypesEnum.TEXT_MARKER, ['fa-solid', 'fa-t']],
            [ChartAnnotationMarkerTypesEnum.CALLOUT_MARKER, ['fa-duotone', 'fa-regular', 'fa-comment-text']]
        ]);
        const textCollectionButton = this.textCollectionButton.nativeElement;
        const textCollectionRibbon = this.textCollectionRibbon.nativeElement;
        this.textCollectionMarkerTypes.forEach((type) => {
            const button = toolbar.markerButtons.find(x => x.dataset.typeName === type);
            if (button) {
                button.innerHTML = '';
                button.classList.add(
                    'agpr-chartAnnotationDialog-toolbarButton',
                    'agpr-chartAnnotationDialog-toolbarButton-priorityWeightClass'
                );
                typesToIconClassesMap.get(type).forEach(x => button.classList.add(x));
                textCollectionRibbon.appendChild(button);
            }
        });
        toolbar.markerButtonBlock.appendChild(textCollectionButton);
        toolbar.markerButtonBlock.appendChild(textCollectionRibbon);
    }

    toggleTextCollection(): void {
        this.textCollectionExpanded = !this.textCollectionExpanded;
        this.actionCollectionExpanded = false;
        this.lineCollectionExpanded = false;
        this.shapeCollectionExpanded = false;
    }

    insertIndicatorCollectionButton(toolbar: any): void {
        if (this.indicatorGroups.length > 0) {
            const indicatorCollectionButton = this.indicatorCollectionDropdown.nativeElement;
            toolbar.markerButtonBlock.appendChild(indicatorCollectionButton);
        }
    }

    handleToolbarResponsiveness(markerArea: any, width: number): void {
        if (width < 430) {
            this.actionCollectionOverflow = true;
            this.insertActionCollectionButton(markerArea.toolbar);
            if (width < 360) {
                this.markerCollectionOverflow = true;
                this.insertMarkerCollectionButton(markerArea.toolbar);
            }
            this.cd.detectChanges();
        }
    }

    handleToolboxIconClassReset(markerArea: any): void {
        const uiContainer = markerArea.toolbox.uiContainer;
        uiContainer.addEventListener('click', (e) => {
            if (!e.target.closest('[title]')) {
                return;
            }

            setTimeout(() => {
                this.styleToolbox(markerArea);
            }, 0);
        });
    }

    insertActionCollectionButton(toolbar: any): void {
        const actionCollectionDataActions = ['delete', 'undo', 'redo'];
        const actionCollectionButton = this.actionCollectionButton.nativeElement;
        const actionCollectionRibbon = this.actionCollectionRibbon.nativeElement;
        const actionButtonBlock = toolbar.uiContainer.children[0];
        actionCollectionDataActions.forEach((type) => {
            const button = (Array.from(actionButtonBlock.children)as HTMLDivElement[]).find(x => x.getAttribute('data-action') === type);
            if (button) {
                actionCollectionRibbon.appendChild(button);
            }
        });
        actionButtonBlock.appendChild(actionCollectionButton);
        actionButtonBlock.appendChild(actionCollectionRibbon);

        this.addOverflownButtons(toolbar, toolbar.markerButtonBlock);
    }

    toggleActionCollection(): void {
        this.actionCollectionExpanded = !this.actionCollectionExpanded;
        this.lineCollectionExpanded = false;
        this.shapeCollectionExpanded = false;
        this.textCollectionExpanded = false;
    }

    insertMarkerCollectionButton(toolbar: any): void {
        const currentMarkerIcon = this.currentMarkerIcon.nativeElement;
        const markerCollectionButton = this.markerCollectionButton.nativeElement;
        const markerCollectionRibbon = this.markerCollectionRibbon.nativeElement;
        const markerButtonBlock = toolbar.markerButtonBlock;

        this.updateCurrentMarkerIconClass();
        this.addOverflownButtons(toolbar, markerCollectionRibbon);
        (Array.from(markerButtonBlock.children) as HTMLElement[]).forEach((x, i) => {
            markerCollectionRibbon.appendChild(x);
        });
        markerButtonBlock.appendChild(currentMarkerIcon);
        markerButtonBlock.appendChild(markerCollectionButton);
        markerButtonBlock.appendChild(markerCollectionRibbon);
    }

    addOverflownButtons(toolbar: any, container: HTMLDivElement): void {
        if (toolbar.markerButtonOverflowBlock.children.length > 0) {
            const freehandMarkerButton = toolbar.markerButtonOverflowBlock.children[0];
            freehandMarkerButton.innerHTML = '';
            freehandMarkerButton.classList.add(
                'agpr-chartAnnotationDialog-toolbarButton',
                'agpr-chartAnnotationDialog-toolbarButton-priorityWeightClass',
                'fa-duotone',
                'fa-regular',
                'fa-marker'
            );
            container.prepend(freehandMarkerButton);
        }
    }

    toggleMarkerCollection(): void {
        this.markerCollectionExpanded = !this.markerCollectionExpanded;
        this.actionCollectionExpanded = false;
        this.lineCollectionExpanded = false;
        this.shapeCollectionExpanded = false;
        this.textCollectionExpanded = false;
    }

    markerSelected(event: any): void {
        this.markerArea = event.markerArea;
        this.lineCollectionExpanded = false;
        this.shapeCollectionExpanded = false;
        this.textCollectionExpanded = false;
        this.setToolboxPanel(event.markerArea);
        const panelRow = event.markerArea.toolbox.panelRow;
        panelRow.style.border = '1px solid #ced4da';
        panelRow.style.backgroundColor = '#ffffff';
        this.updateCurrentMarkerIconClass();
    }

    markerDeselected(event: any): void {
        this.markerArea.toolbox.activePanel = undefined;
        this.updateCurrentMarkerIconClass();
    }

    styleActionButtons(): void {
        const actionButtonBlock = this.markerArea.toolbar.uiContainer.children[0];
        const deleteButton = actionButtonBlock.querySelector('div[data-action="delete"]');
        if (deleteButton) {
            if (this.markerArea.currentMarker?.isSelected) {
                deleteButton.classList.remove('agpr-chartAnnotationDialog-toolbarButton--disabled');
            } else {
                deleteButton.classList.add('agpr-chartAnnotationDialog-toolbarButton--disabled');
            }
        }
        const undoButton = actionButtonBlock.querySelector('div[data-action="undo"]');
        if (undoButton) {
            if (this.markerArea.isUndoPossible) {
                undoButton.classList.remove('agpr-chartAnnotationDialog-toolbarButton--disabled');
            } else {
                undoButton.classList.add('agpr-chartAnnotationDialog-toolbarButton--disabled');
            }
        }
        const redoButton = actionButtonBlock.querySelector('div[data-action="redo"]');
        if (redoButton) {
            if (this.markerArea.isRedoPossible) {
                redoButton.classList.remove('agpr-chartAnnotationDialog-toolbarButton--disabled');
            } else {
                redoButton.classList.add('agpr-chartAnnotationDialog-toolbarButton--disabled');
            }
        }
    }

    updateCurrentMarkerIconClass(): void {
        if (this.markerArea.currentMarker) {
            const iconClass = this.markerTypesToMarkerIconClassesMap.get(this.markerArea.currentMarker.typeName)
                + (this.lineCollectionMarkerTypes.includes(this.markerArea.currentMarker.typeName) ?
                    ' agpr-chartAnnotationDialog-markerCollection-currentMarker--rotated' : '');
            this.currentMarkerIconClass = iconClass;
        } else {
            this.currentMarkerIconClass = this.markerTypesToMarkerIconClassesMap.get(ChartAnnotationMarkerTypesEnum.FREEHAND_MARKER);
        }
        this.cd.detectChanges();
    }

    styleToolbox(markerArea: any): void {
        if (!markerArea) {
            return;
        }
        markerArea.toolbox.uiContainer.style.marginTop = '14px';
        const markerTypesToIconClassesMap: Map<ChartAnnotationMarkerTypesEnum, string[][]> = new Map([
            [
                ChartAnnotationMarkerTypesEnum.FREEHAND_MARKER,
                [
                    ['fa-duotone', 'fa-regular', 'fa-palette'],
                    ['fa-kit', 'fa-agpr-line-weight']
                ]
            ],
            [
                ChartAnnotationMarkerTypesEnum.LINE_MARKER,
                [
                    ['fa-duotone', 'fa-regular', 'fa-palette'],
                    ['fa-kit', 'fa-agpr-line-weight'],
                    ['fa-kit', 'fa-agpr-line-style']
                ]
            ],
            [
                ChartAnnotationMarkerTypesEnum.ARROW_MARKER,
                [
                    ['fa-duotone', 'fa-regular', 'fa-palette'],
                    ['fa-kit', 'fa-agpr-line-weight'],
                    ['fa-kit', 'fa-agpr-line-style'],
                    ['fa-solid', 'fa-regular', 'fa-arrows-left-right']
                ]
            ],
            [
                ChartAnnotationMarkerTypesEnum.CURVE_MARKER,
                [
                    ['fa-duotone', 'fa-regular', 'fa-palette'],
                    ['fa-kit', 'fa-agpr-line-weight'],
                    ['fa-kit', 'fa-agpr-line-style']
                ]
            ],
            [
                ChartAnnotationMarkerTypesEnum.FRAME_MARKER,
                [
                    ['fa-duotone', 'fa-regular', 'fa-palette'],
                    ['fa-kit', 'fa-agpr-line-weight'],
                    ['fa-kit', 'fa-agpr-line-style']
                ]
            ],
            [
                ChartAnnotationMarkerTypesEnum.ELLIPSE_MARKER,
                [
                    ['fa-kit', 'fa-agpr-border-color'],
                    ['fa-duotone', 'fa-regular', 'fa-fill-drip'],
                    ['fa-kit', 'fa-agpr-line-weight'],
                    ['fa-kit', 'fa-agpr-line-style'],
                    ['fa-kit', 'fa-agpr-opacity']
                ]
            ],
            [
                ChartAnnotationMarkerTypesEnum.HIGHLIGHT_MARKER,
                [
                    ['fa-duotone', 'fa-regular', 'fa-palette'],
                    ['fa-kit', 'fa-agpr-opacity']
                ]
            ],
            [
                ChartAnnotationMarkerTypesEnum.TEXT_MARKER,
                [
                    ['fa-kit', 'fa-agpr-text-color'],
                    ['fa-kit', 'fa-agpr-font']
                ]
            ],
            [
                ChartAnnotationMarkerTypesEnum.CALLOUT_MARKER,
                [
                    ['fa-kit', 'fa-agpr-text-color'],
                    ['fa-duotone', 'fa-regular', 'fa-fill-drip'],
                    ['fa-kit', 'fa-agpr-font']
                ]
            ]
        ]);

        const toolboxDiv = markerArea.toolbox.uiContainer;

        const panelBlock = toolboxDiv.querySelector('[class*="_toolbox-panel-row"]');
        if (panelBlock) {
            panelBlock.style.position = 'static';
            panelBlock.style.border = 'none';
            panelBlock.style.width = this.width  + 'px';
        }

        const buttonBlock = markerArea.toolbox.uiContainer.querySelector('[class*="_toolbox-button-row"]');
        buttonBlock?.classList.add('agpr-chartAnnotationDialog-toolboxBlock');
        if (markerArea.currentMarker) {
            const icons = markerTypesToIconClassesMap.get(markerArea.currentMarker.typeName);
            if (icons) {
                icons.forEach((x, i) => {
                    const button = buttonBlock?.querySelectorAll('[class*="_toolbox_button"]')[i];
                    if (button) {
                        button.innerHTML = '';
                        button.classList.add(
                            'agpr-chartAnnotationDialog-toolboxButton',
                            'agpr-chartAnnotationDialog-toolboxButton-priorityWeightClass'
                        );
                        x.forEach(className => button.classList.add(className));
                    }
                });
            }
        }

        if (markerArea.toolbox.activePanel && markerArea.toolbox.panels?.length > 0) {
            if (markerArea.toolbox.activePanel instanceof ChartAnnotationColorPickerPanel) {
                toolboxDiv.style.height = '415px';
                panelBlock.style.height = '365px';
                panelBlock.style.backgroundColor = 'transparent';
            } else {
                toolboxDiv.style.height = '100px';
                panelBlock.style.height = '50px';
                panelBlock.style.backgroundColor = '#ffffff';

            }
            toolboxDiv.style.border = 'none';
            if (buttonBlock) {
                buttonBlock.style.borderTop = '1px solid #ced4da';
            }
        } else {
            toolboxDiv.style.height = '50px';
            toolboxDiv.style.borderTop = '1px solid #ced4da';
            if (panelBlock) {
                panelBlock.style.display = 'none';
            }
            if (buttonBlock) {
                buttonBlock.style.border = 'none';
            }
        }
    }

    styleControlRect(marker: any): void {
        if (marker.controlRect) {
            marker.controlRect.style.stroke = '#2b4e66';
        }
    }

    styleGrip(marker: any): void {
        let grips: ResizeGrip[];
        switch (marker.typeName) {
            case ChartAnnotationMarkerTypesEnum.LINE_MARKER:
            case ChartAnnotationMarkerTypesEnum.ARROW_MARKER:
                grips = [marker.grip1, marker.grip2];
                break;
            case ChartAnnotationMarkerTypesEnum.CURVE_MARKER:
                grips = [marker.grip1, marker.grip2, marker.curveGrip];
                break;
            case ChartAnnotationMarkerTypesEnum.FREEHAND_MARKER:
            case ChartAnnotationMarkerTypesEnum.FRAME_MARKER:
            case ChartAnnotationMarkerTypesEnum.ELLIPSE_MARKER:
            case ChartAnnotationMarkerTypesEnum.HIGHLIGHT_MARKER:
            case ChartAnnotationMarkerTypesEnum.TEXT_MARKER:
            case ChartAnnotationMarkerTypesEnum.CALLOUT_MARKER:
                grips = [
                    marker.rotatorGrip,
                    marker.controlGrips.topLeft,
                    marker.controlGrips.topCenter,
                    marker.controlGrips.topRight,
                    marker.controlGrips.centerLeft,
                    marker.controlGrips.centerRight,
                    marker.controlGrips.bottomLeft,
                    marker.controlGrips.bottomCenter,
                    marker.controlGrips.bottomRight
                ];
                if (marker.typeName === ChartAnnotationMarkerTypesEnum.CALLOUT_MARKER) {
                    grips.push(marker.tipGrip);
                }
                break;
        }
        grips.forEach(grip => {
            const visual = grip.visual.children[1] as SVGElement;
            visual.style.fill = '#2b4e66';
            visual.style.fillOpacity = '1';
            visual.style.stroke = '#2b4e66';
            visual.style.strokeOpacity = '1';
            visual.style.cx = '5';
            visual.style.cy = '5';
            visual.style.r = '5';
        });
    }

    setToolboxPanel(markerArea: any): void {
        const typeName = markerArea.currentMarker.typeName;
        switch (typeName) {
            case ChartAnnotationMarkerTypesEnum.FREEHAND_MARKER:
                this.setColorPanel(markerArea, this.freehandColorPicker);
                this.setLineWidthPanel(markerArea, this.freehandLineWidthRadioGroup.nativeElement);
                break;
            case ChartAnnotationMarkerTypesEnum.LINE_MARKER:
                this.setStrokePanel(markerArea, this.lineStrokeColorPicker);
                this.setStrokeWidthPanel(markerArea, this.lineStrokeWidthRadioGroup.nativeElement);
                this.setLineStylePanel(markerArea, this.lineLineStyleRadioGroup.nativeElement);
                break;
            case ChartAnnotationMarkerTypesEnum.ARROW_MARKER:
                this.setStrokePanel(markerArea, this.arrowStrokeColorPicker);
                this.setStrokeWidthPanel(markerArea, this.arrowStrokeWidthRadioGroup.nativeElement);
                this.setLineStylePanel(markerArea, this.arrowLineStyleRadioGroup.nativeElement);
                this.setArrowTypePanel(markerArea, this.arrowArrowTypeRadioGroup.nativeElement);
                break;
            case ChartAnnotationMarkerTypesEnum.CURVE_MARKER:
                this.setStrokePanel(markerArea, this.curveStrokeColorPicker);
                this.setStrokeWidthPanel(markerArea, this.curveStrokeWidthRadioGroup.nativeElement);
                this.setLineStylePanel(markerArea, this.curveLineStyleRadioGroup.nativeElement);
                break;
            case ChartAnnotationMarkerTypesEnum.FRAME_MARKER:
                this.setStrokePanel(markerArea, this.frameStrokeColorPicker);
                this.setStrokeWidthPanel(markerArea, this.frameStrokeWidthRadioGroup.nativeElement);
                this.setLineStylePanel(markerArea, this.frameLineStyleRadioGroup.nativeElement);
                break;
            case ChartAnnotationMarkerTypesEnum.ELLIPSE_MARKER:
                this.setStrokePanel(markerArea, this.ellipseStrokeColorPicker);
                this.setFillPanel(markerArea, this.ellipseFillColorPicker);
                this.setStrokeWidthPanel(markerArea, this.ellipseStrokeWidthRadioGroup.nativeElement);
                this.setLineStylePanel(markerArea, this.ellipseLineStyleRadioGroup.nativeElement);
                this.setOpacityPanel(markerArea, this.ellipseOpacitySliderInput);
                break;
            case ChartAnnotationMarkerTypesEnum.HIGHLIGHT_MARKER:
                this.setFillPanel(markerArea, this.highlightFillColorPicker);
                this.setOpacityPanel(markerArea, this.highlightOpacitySliderInput);
                break;
            case ChartAnnotationMarkerTypesEnum.TEXT_MARKER:
                this.setColorPanel(markerArea, this.textColorPicker);
                this.setFontFamilyPanel(markerArea, this.textFontFamilyDropdown);
                break;
            case ChartAnnotationMarkerTypesEnum.CALLOUT_MARKER:
                this.setColorPanel(markerArea, this.calloutColorPicker);
                this.setBgColorPanel(markerArea, this.calloutBgColorPicker);
                this.setFontFamilyPanel(markerArea, this.calloutFontFamilyDropdown);
                break;
        }
    }

    setColorPanel(markerArea: any, element: any): void {
        markerArea.currentMarker.colorPanel = new ChartAnnotationColorPickerPanel(
            this.localizationService.transform('Reports.annotation.color'),
            [],
            markerArea.currentMarker.color,
            null,
            element,
            markerArea
        );
        markerArea.toolbox.setPanelButtons(markerArea.currentMarker.toolboxPanels);
        markerArea.currentMarker.colorPanel.onColorChanged = markerArea.currentMarker.setColor;
    }

    setStrokePanel(markerArea: any, element: any): void {
        markerArea.currentMarker.strokePanel = new ChartAnnotationColorPickerPanel(
            this.localizationService.transform('Reports.annotation.color'),
            [],
            markerArea.currentMarker.strokeColor,
            null,
            element,
            markerArea
        );
        markerArea.toolbox.setPanelButtons(markerArea.currentMarker.toolboxPanels);
        markerArea.currentMarker.strokePanel.onColorChanged = markerArea.currentMarker.setStrokeColor;
    }

    setFillPanel(markerArea: any, element: any): void {
        markerArea.currentMarker.fillPanel = new ChartAnnotationColorPickerPanel(
            this.localizationService.transform('Reports.annotation.color'),
            [],
            markerArea.currentMarker.fillColor,
            null,
            element,
            markerArea
        );
        markerArea.toolbox.setPanelButtons(markerArea.currentMarker.toolboxPanels);
        markerArea.currentMarker.fillPanel.onColorChanged = markerArea.currentMarker.setFillColor;
    }

    setBgColorPanel(markerArea: any, element: any): void {
        markerArea.currentMarker.bgColorPanel = new ChartAnnotationColorPickerPanel(
            this.localizationService.transform('Reports.annotation.color'),
            [],
            markerArea.currentMarker.bgColor,
            null,
            element,
            markerArea
        );
        markerArea.toolbox.setPanelButtons(markerArea.currentMarker.toolboxPanels);
        markerArea.currentMarker.bgColorPanel.onColorChanged = markerArea.currentMarker.setBgColor;
    }

    setFontFamilyPanel(markerArea: any, element: any): void {
        this.fontFamilyDropdownOptions = textStyleFontDropdownOptions.map(x => {
            return new DropdownOptionModel(
                this.localizationService.transform(x.label),
                x.value
            );
        });
        markerArea.currentMarker.fontFamilyPanel = new ChartAnnotationFontFamilyPanel(
            this.localizationService.transform('Reports.annotation.fontFamily'),
            [],
            markerArea.currentMarker.fontFamily,
            null,
            element
        );
        markerArea.toolbox.setPanelButtons(markerArea.currentMarker.toolboxPanels);
        markerArea.currentMarker.fontFamilyPanel.onFontChanged = markerArea.currentMarker.setFont;
    }

    setLineWidthPanel(markerArea: any, element: any): void {
        markerArea.currentMarker.lineWidthPanel = new ChartAnnotationLineWidthPanel(
            this.localizationService.transform('Reports.annotation.lineWidth'),
            [],
            markerArea.currentMarker.lineWidth,
            null,
            element,
            this.radioComponents
        );
        markerArea.toolbox.setPanelButtons(markerArea.currentMarker.toolboxPanels);
        markerArea.currentMarker.lineWidthPanel.onWidthChanged = markerArea.currentMarker.setLineWidth;
    }

    setStrokeWidthPanel(markerArea: any, element: any): void {
        markerArea.currentMarker.strokeWidthPanel = new ChartAnnotationLineWidthPanel(
            this.localizationService.transform('Reports.annotation.lineWidth'),
            [],
            markerArea.currentMarker.strokeWidth,
            null,
            element,
            this.radioComponents
        );
        markerArea.toolbox.setPanelButtons(markerArea.currentMarker.toolboxPanels);
        markerArea.currentMarker.strokeWidthPanel.onWidthChanged = markerArea.currentMarker.setStrokeWidth;
    }

    setLineStylePanel(markerArea: any, element: any): void {
        markerArea.currentMarker.strokeStylePanel = new ChartAnnotationLineStylePanel(
            this.localizationService.transform('Reports.annotation.lineStyle'),
            [],
            markerArea.currentMarker.strokeDasharray,
            null,
            element,
            this.radioComponents
        );
        markerArea.toolbox.setPanelButtons(markerArea.currentMarker.toolboxPanels);
        markerArea.currentMarker.strokeStylePanel.onStyleChanged = markerArea.currentMarker.setStrokeDasharray;
    }

    setArrowTypePanel(markerArea: any, element: any): void {
        markerArea.currentMarker.arrowTypePanel = new ChartAnnotationArrowTypePanel(
            this.localizationService.transform('Reports.annotation.arrowType'),
            markerArea.currentMarker.arrowType,
            null,
            element,
            this.radioComponents
        );
        markerArea.toolbox.setPanelButtons(markerArea.currentMarker.toolboxPanels);
        markerArea.currentMarker.arrowTypePanel.onArrowTypeChanged = markerArea.currentMarker.setArrowType;
    }

    setOpacityPanel(markerArea: any, element: SliderInputComponent): void {
        markerArea.currentMarker.opacityPanel = new ChartAnnotationOpacityPanel(
            this.localizationService.transform('Reports.annotation.opacity'),
            [],
            markerArea.currentMarker.opacity,
            null,
            element
        );
        markerArea.toolbox.setPanelButtons(markerArea.currentMarker.toolboxPanels);
        markerArea.currentMarker.opacityPanel.onOpacityChanged = markerArea.currentMarker.setOpacity;
    }

    radioChanged(
        attr: any,
        markerType: ChartAnnotationMarkerTypesEnum,
        styleType: ChartAnnotationMarkerStyleTypesEnum
    ): void {
        const markerTypesToVariableNameSnippetsMap = new Map<ChartAnnotationMarkerTypesEnum, string>([
            [ChartAnnotationMarkerTypesEnum.FREEHAND_MARKER, 'Freehand'],
            [ChartAnnotationMarkerTypesEnum.LINE_MARKER, 'Line'],
            [ChartAnnotationMarkerTypesEnum.ARROW_MARKER, 'Arrow'],
            [ChartAnnotationMarkerTypesEnum.CURVE_MARKER, 'Curve'],
            [ChartAnnotationMarkerTypesEnum.FRAME_MARKER, 'Frame'],
            [ChartAnnotationMarkerTypesEnum.ELLIPSE_MARKER, 'Ellipse']
        ]);
        let property;
        switch (styleType) {
            case ChartAnnotationMarkerStyleTypesEnum.LINE_WIDTH:
                property = `selected${markerTypesToVariableNameSnippetsMap.get(markerType)}LineWidth`;
                this.markerArea.currentMarker.lineWidthPanel.currentLineWidth = attr;
                this.markerArea.currentMarker.lineWidthPanel.onWidthChanged(attr);
                break;
            case ChartAnnotationMarkerStyleTypesEnum.STROKE_WIDTH:
                property = `selected${markerTypesToVariableNameSnippetsMap.get(markerType)}StrokeWidth`;
                this.markerArea.currentMarker.strokeWidthPanel.currentLineWidth = attr;
                this.markerArea.currentMarker.strokeWidthPanel.onWidthChanged(
                    attr + (markerType === ChartAnnotationMarkerTypesEnum.ARROW_MARKER ? '' : 'px')
                );
                break;
            case ChartAnnotationMarkerStyleTypesEnum.LINE_STYLE:
                property = `selected${markerTypesToVariableNameSnippetsMap.get(markerType)}LineStyle`;
                this.markerArea.currentMarker.strokeStylePanel.currentLineStyle = attr;
                this.markerArea.currentMarker.strokeStylePanel.onStyleChanged(attr);
                break;
            case ChartAnnotationMarkerStyleTypesEnum.ARROW_TYPE:
                property = `selected${markerTypesToVariableNameSnippetsMap.get(markerType)}ArrowType`;
                this.markerArea.currentMarker.arrowTypePanel.currentArrowType = attr;
                this.markerArea.currentMarker.arrowTypePanel.onArrowTypeChanged(attr);
                break;
        }
        this[property] = attr;
    }

    updateIndicators(indicators: any[] = []): void {
        this.selectedIndicators = indicators;
        const nonIndicatorChartSeries = this.originalChartSeries.filter(x => !x.indicatorTargetIndex && x.indicatorTargetIndex !== 0);
        this.chartAnnotationIndicatorsService.updateIndicators(this.chart, nonIndicatorChartSeries, indicators.map(x => x.value));
        requestAnimationFrame(() => this.reportSeries.forEach(series => series.dataProvider.next(series.dataProvider.getValue())));
    }

    closeColorPicker(event: any): void {
        if (!event || (![...event.target.classList].some(x => x.includes('_toolbox_button')) && !this.isWithinColorPicker(event))
            && this.markerArea.toolbox.activePanel instanceof ChartAnnotationColorPickerPanel
        ) {
            const toolbox = this.markerArea.toolbox;
            toolbox.activePanel = undefined;
            setTimeout(() => {
                if (toolbox.panelRow) {
                    toolbox.panelRow.style.display = 'none';
                }
            }, 200);
            this.styleToolbox(this.markerArea);
        }
    }

    private isWithinColorPicker(event: any): boolean {
        const colorPickerClassNames = [
            'agpr-colorGradientPicker',
            'agpr-dropdown-input',
            'agpr-dropdown-item',
            'agpr-dropdown-panel-item-label',
            'agpr-dropdown-panel-item-mainLabel',
            'agpr-dropdown-triggerIcon'
        ];
        const targetClassList = [...event.target.classList];
        // const targetParentClassList = [...event.target.parentElement?.classList];

        return targetClassList.some(x => colorPickerClassNames.some(y => y.includes(x)));
    }
}
